import { useEffect, useState } from "react";
import classNames from "classnames";
import { PrimaryButton } from "components/Buttons";
import {
  assetLabel,
  LONG_WINDOW,
  ROLLING_WINDOW,
  SensorStatus,
  SensorView,
  STATUS_EXPLANATIONS,
  STATUS_LABELS,
} from "../sensorHealth.utility";
import { DetectionGauge } from "./DetectionGauge";
import { SensorSparkline } from "./SensorSparkline";

/** Pill styling keyed on the finer status, so "nothing detected" isn't dressed up as green. */
const STATUS_PILL: Record<SensorStatus, string> = {
  detecting: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  "nothing-detected": "bg-slate-100 text-slate-600 ring-slate-500/20",
  "no-signal": "bg-amber-50 text-amber-700 ring-amber-600/20",
  flickering: "bg-amber-50 text-amber-700 ring-amber-600/20",
  "not-responding": "bg-red-50 text-red-700 ring-red-600/20",
  unlinked: "bg-gray-100 text-gray-600 ring-gray-500/20",
};

const STATUS_DOT: Record<SensorStatus, string> = {
  detecting: "bg-emerald-500",
  "nothing-detected": "bg-slate-400",
  "no-signal": "bg-amber-500",
  flickering: "bg-amber-500",
  "not-responding": "bg-red-500",
  unlinked: "bg-gray-400",
};

const READING_COLOR: Record<SensorStatus, string> = {
  detecting: "text-emerald-600",
  "nothing-detected": "text-gray-700",
  "no-signal": "text-amber-600",
  flickering: "text-amber-600",
  "not-responding": "text-gray-400",
  unlinked: "text-gray-700",
};

const ToolIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.4 15.6 4 23a2 2 0 0 1-3-3l7.4-7.4a5.5 5.5 0 0 1 6.9-6.9l-3 3 2 2 3-3a5.5 5.5 0 0 1-6.9 6.9Z"
    />
  </svg>
);

const ToolCard = ({ sensor }: { sensor: SensorView }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const asset = sensor.linkedAsset;

  if (!asset) {
    return (
      <div className="flex items-center gap-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-3.5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-white text-gray-300 ring-1 ring-gray-200">
          <ToolIcon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-700">No tool linked yet</p>
          <p className="mt-0.5 text-xs text-gray-500">
            This sensor is reporting normally. Link a tool during calibration to turn on presence detection.
          </p>
        </div>
      </div>
    );
  }

  const imageUrl = asset.catalogProduct?.imageUrl;
  const mpn = asset.catalogProduct?.mpn;
  const meta = [mpn && `MPN ${mpn}`, asset.serialNumber && `Serial ${asset.serialNumber}`].filter(Boolean).join(" · ");

  return (
    <div
      className={classNames(
        "flex items-center gap-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100/60 p-3.5 ring-1 ring-gray-200/70",
        !sensor.boardResponding && "opacity-70",
      )}
    >
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white text-gray-400 ring-1 ring-gray-200">
        {imageUrl && !imageFailed ? (
          <img
            src={imageUrl}
            alt={assetLabel(asset)}
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <ToolIcon className="h-8 w-8" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold text-gray-900">{assetLabel(asset)}</p>
        {meta && <p className="mt-0.5 truncate text-xs text-gray-500">{meta}</p>}
      </div>
    </div>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-1.5 ring-1 ring-gray-100">
    <span className="font-mono text-sm font-semibold tabular-nums text-gray-900">{value}</span>
    <span className="ml-1.5 text-[11px] text-gray-400">{label}</span>
  </div>
);

type SensorDetailModalProps = {
  sensor: SensorView;
  onClose: () => void;
  /** Whether a live check is currently polling — drives the live indicator. */
  running?: boolean;
};

/**
 * A controlled, self-contained overlay (not the global modal system) so it can be fed the current
 * SensorView on every poll and update live while the check keeps running. The reading is plotted
 * against its detection threshold in a gauge and trend chart that share one data-driven scale.
 */
export const SensorDetailModal = ({ sensor, onClose, running = false }: SensorDetailModalProps) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { status, bucket, threshold, history, boardResponding } = sensor;
  const frozen = !boardResponding;
  const live = running && boardResponding;

  const observedMax = history.length ? Math.max(...history) : 0;
  const scaleMax = Math.max(observedMax, threshold ?? 0, 32) * 1.1;

  const context = [
    threshold != null && `threshold ${Math.round(threshold)}`,
    `avg ${sensor.rollingAvg.toFixed(0)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      {/* Stop propagation so clicks inside the card don't close it. */}
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className={classNames(
                "h-2.5 w-2.5 shrink-0 rounded-full",
                live ? "bg-emerald-500 motion-safe:animate-pulse" : "bg-gray-300",
              )}
              aria-hidden
            />
            <h2 className="truncate font-mono text-[15px] font-semibold tracking-tight text-gray-900">
              Board {sensor.boardId} · Sensor #{sensor.position}
            </h2>
          </div>
          <span
            className={classNames(
              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset",
              STATUS_PILL[status],
            )}
          >
            <span className={classNames("h-1.5 w-1.5 rounded-full", STATUS_DOT[status])} />
            {STATUS_LABELS[status]}
          </span>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-auto px-6 py-5">
          {frozen && (
            <div className="flex items-start gap-2.5 rounded-xl bg-red-50 p-3.5 text-sm text-red-700 ring-1 ring-red-100">
              <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              </svg>
              <p>This sensor&apos;s board stopped reporting. Everything below is the last data received before it went quiet.</p>
            </div>
          )}

          <ToolCard sensor={sensor} />

          <div>
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className={classNames("font-mono text-5xl font-semibold leading-none tabular-nums", READING_COLOR[status])}>
                    {Math.round(sensor.latest)}
                  </span>
                  {context && <span className="font-mono text-xs tabular-nums text-gray-400">{context}</span>}
                </div>
                <p className="mt-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
                  {frozen ? "Last reading" : "Live reading"}
                </p>
              </div>
              {frozen && (
                <span className="shrink-0 rounded-md bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-500 ring-1 ring-gray-200">
                  Frozen
                </span>
              )}
              {!frozen && sensor.flickering && status !== "flickering" && (
                <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  Unstable
                </span>
              )}
            </div>

            <div className="mt-4">
              <DetectionGauge
                reading={sensor.latest}
                threshold={threshold}
                scaleMax={scaleMax}
                bucket={bucket}
                frozen={frozen}
              />
            </div>

            <p className="mt-3 text-sm text-gray-600">{STATUS_EXPLANATIONS[status]}</p>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Trend · {frozen ? "before it went quiet" : "this session"}
              </p>
              <p className="font-mono text-[11px] tabular-nums text-gray-400">{history.length} readings</p>
            </div>
            <div className="h-32 w-full overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-100">
              <SensorSparkline values={history} scaleMax={scaleMax} threshold={threshold} bucket={bucket} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Metric label={`avg · last ${ROLLING_WINDOW}`} value={sensor.rollingAvg.toFixed(1)} />
              <Metric label={`avg · last ${LONG_WINDOW}`} value={sensor.longAvg.toFixed(1)} />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 justify-end border-t border-gray-100 px-6 py-3.5">
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </div>
      </div>
    </div>
  );
};
